import { defineStore } from 'pinia'
import { ElMessage } from 'element-plus'
import { loadAsync } from 'jszip'
import i18n from '~/src/lang'
import { useCommonStore } from '../useCommonStore'
import { useListStore } from '../useListStore'
import { useModalStore } from '../useModalStore'
import uploadQueue, { FILE_TYPES, SUPPORT_TYPES } from './uploadQueue'
import { DEV_BASE_URL } from '../../../scripts/utils'

const t = i18n.global.t
let _uploadInput = null
let _cancelAdd = null

export const useUploadStore = defineStore('upload', {
  state: () => ({
    isAdding: false,
    activeFile: null,
    addingFiles: [],
    uploadingFiles: [],
    latestTime: 0
  }),
  getters: {
    currentAddingFiles: state => {
      return state.activeFile ? [state.activeFile] : state.addingFiles
    },
    currentUploadingFiles: state => {
      const commonStore = useCommonStore()
      return state.uploadingFiles.filter(
        item => item.folderId == commonStore.folderId && item.space == commonStore.space
      )
    },
    isUploading: state => {
      return (
        state.uploadingFiles.filter(item => ['analyzing', 'uploading'].includes(item.status))
          .length > 0
      )
    }
  },
  actions: {
    setIsAdding(isAdding = false) {
      if (isAdding !== this.isAdding) {
        this.isAdding = isAdding
        if (isAdding) {
          _uploadInput.setAttribute('disabled', 'disabled')
        } else {
          _uploadInput.removeAttribute('disabled')
        }
      }
    },
    init(id = 'models_upload', needReset = false) {
      uploadQueue.init()
      if (_uploadInput) {
        if (!needReset) return
        this.clear()
      }
      if (id && !_uploadInput) {
        _uploadInput = document.createElement('input')
        _uploadInput.setAttribute('type', 'file')
        _uploadInput.setAttribute('id', id)
        _uploadInput.setAttribute('accept', ['zip', ...FILE_TYPES].map(ext => `.${ext}`).join(','))
        _uploadInput.setAttribute(
          'style',
          'position: absolute; width: 0; height: 0; opacity: 0; top: 0; left: 0; user-select: none; pointer-events: none;'
        )
        _uploadInput.addEventListener('change', this.addFileByInput)
        document.body.appendChild(_uploadInput)
      }
    },
    clear() {
      this.isAdding = false
      this.activeFile = null
      this.addingFiles = []
      this.uploadingFiles = []
      if (_uploadInput) {
        _uploadInput.removeEventListener('change', this.addFileByInput)
        _uploadInput.remove()
        _uploadInput = null
      }
      uploadQueue.clear()
    },
    clearAddCancel() {
      if (_cancelAdd) {
        _cancelAdd()
        _cancelAdd = null
      }
    },
    clearAddingFiles() {
      this.addingFiles = []
      this.clearAddCancel()
      this.setIsAdding(false)
      if (_uploadInput) {
        _uploadInput.value = ''
      }
      useListStore().handleTooltips()
    },
    clearAll() {
      this.clearAddingFiles()
      this.activeFile = null
    },
    openUploadPicker() {
      if (!_uploadInput) this.init()
      if (_uploadInput.showPicker) {
        _uploadInput.showPicker()
      } else {
        _uploadInput.click()
      }
    },
    async checkFileType(file) {
      const suffix = file.name.split('.').pop()?.toLowerCase()
      if (
        suffix === 'zip' &&
        ['application/zip', 'application/x-zip', 'application/x-zip-compressed'].includes(file.type)
      ) {
        const zip = await loadAsync(file)
        const keys = Object.keys(zip.files)
        const supportFiles = []
        let type = ''
        let modelFile = null
        let mtlFile = null
        for (let i = 0; i < keys.length; i++) {
          const key = keys[i]
          const zipFile = zip.files[key]
          const zipType = key?.toLowerCase() || ''
          if (zipFile && !zipType.startsWith('__macosx')) {
            const modelType = FILE_TYPES.find(type => zipType.endsWith(`.${type}`))
            if (modelType) {
              const f = await zipFile.async('blob')
              type = modelType
              modelFile = f
            } else if (zipType.endsWith('.mtl')) {
              const f = await zipFile.async('blob')
              mtlFile = f
            } else if (SUPPORT_TYPES.findIndex(type => zipType.endsWith(`.${type}`)) > -1) {
              const f = await zipFile.async('blob')
              supportFiles.push({
                name: zipFile.name,
                file: f
              })
            }
          }
        }
        return {
          type,
          modelFile,
          mtlFile,
          supportFiles,
          isZip: true
        }
      }
      return FILE_TYPES.includes(suffix)
        ? { type: suffix, modelFile: file }
        : { type: '', modelFile: null }
    },
    async generateFileKey() {
      try {
        const fileKey = Math.random().toString(36).substring(2, 6)
        const res = await fetch(`${DEV_BASE_URL}webModels/share/status`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('SCANAT_TOKEN')
          },
          body: JSON.stringify({
            fileKey
          })
        })
        const result = await res?.json()
        if (result?.code === 806) {
          return fileKey
        } else {
          return this.generateFileKey()
        }
      } catch {
        return this.generateFileKey()
      }
    },
    async _addFile(file, info = {}) {
      if (!this.isAdding) {
        try {
          if (file) {
            this.setIsAdding(true)
            const commonStore = useCommonStore()
            const listStore = useListStore()
            if (file.size > 1024 * 1024 * 500) {
              ElMessage({ message: t('import.fileSizeLimit'), type: 'warning' })
            } else {
              const { type, modelFile, mtlFile, supportFiles, isZip } = await this.checkFileType(
                file
              )
              if (info.cancelled) return
              if (type) {
                const name = file.name.trim().slice(0, -(isZip ? 4 : type.length + 1))
                if (name.length <= 20) {
                  if (this.activeFile) {
                    this.activeFile = {
                      ...this.activeFile,
                      name,
                      type,
                      file,
                      modelFile,
                      mtlFile,
                      supportFiles,
                      isZip
                    }
                  } else if (this.addingFiles.length === 1) {
                    // TODO vicky: 后续支持添加多个文件直接用下个分支
                    this.addingFiles[0] = {
                      ...this.addingFiles[0],
                      name,
                      type,
                      file,
                      modelFile,
                      mtlFile,
                      supportFiles,
                      isZip
                    }
                  } else {
                    const info = {
                      name,
                      type,
                      file,
                      modelFile,
                      mtlFile,
                      supportFiles,
                      isZip,
                      folderId: commonStore.folderId,
                      space: commonStore.space,
                      fileKey: await this.generateFileKey()
                    }
                    if (info.cancelled) return
                    this.addingFiles = [info]
                  }
                  listStore.handleTooltips()
                } else {
                  ElMessage({
                    message: t('error.nameTooLong'),
                    type: 'warning'
                  })
                }
              } else {
                ElMessage({
                  message: t(isZip ? 'file.import.error.invalidFile' : 'import.wrongFormat'),
                  type: 'warning'
                })
              }
            }
            if (_uploadInput) {
              _uploadInput.value = ''
            }
          }
        } finally {
          this.setIsAdding(false)
        }
      }
    },
    addFile(file) {
      if (!this.isAdding) {
        const info = { cancelled: false }
        this._addFile(file, info)
        return () => {
          info.cancelled = true
        }
      }
    },
    addFileByInput() {
      if (_uploadInput) {
        const file = _uploadInput.files[0]
        this.addFileByDrop(file)
      }
    },
    addFileByDrop(file) {
      const cancelAdd = this.addFile(file)
      if (cancelAdd) {
        this.clearAddCancel()
        _cancelAdd = cancelAdd
      }
    },
    appendAddingFiles() {
      if (this.activeFile) {
        this.updateUploadingFile(this.activeFile, {
          ...this.activeFile,
          status: 'waiting',
          percent: 0
        })
      } else {
        this.uploadingFiles.push(...this.addingFiles)
      }
      uploadQueue.appendQueue([...this.currentAddingFiles])
      this.clearAll()
    },
    restartUploadingFile(file) {
      this.activeFile = file
      useModalStore().openModal('uploadInfo', {
        title: t('import.model')
      })
    },
    updateUploadingFile(info, data = {}) {
      const index = this.uploadingFiles.findIndex(item => item.fileKey === info.fileKey)
      if (index > -1) {
        const newInfo = { ...this.uploadingFiles[index], ...data }
        this.uploadingFiles[index] = newInfo
        if (data.status === 'success') {
          this.latestTime = Date.now()
          const index = this.uploadingFiles.findIndex(item => item.fileKey === info.fileKey)
          if (index > -1) this.uploadingFiles.splice(index, 1)
        }
      }
    }
  }
})
